/**
 * API v3 Verification History Client
 * Fetches from GET /api/v3/history/verifications (verifications table)
 * Used by /history/checks page for the "API" tab
 */

import axiosInstance from '@/lib/api/axios-instance'

export interface V3Verification {
  id: string                   // verification_id
  type: 'verification'
  method: string               // 'image' | 'qr-code' | 'base64' | 'url'
  status: 'success' | 'failed'
  amount: number | null
  transactionId: string | null
  transactionDate: string | null
  senderBank: string | null
  senderName: string | null
  senderAccount: string | null
  receiverAccount: string | null
  receiverName: string | null
  bankCode: string | null
  bankVerified: boolean
  createdAt: string
  processingTime: number | null
  credits: number
  reason?: string
}

export interface V3Generation {
  id: string                   // generation_id
  type: 'generation'
  method: string               // 'promptpay' | 'bill-payment'
  status: 'success' | 'failed'
  amount: number | null
  accountType: string | undefined   // for promptpay: 'MOBILE' | 'NATIONAL_ID' etc.
  billerId: string | undefined      // for bill-payment
  createdAt: string
  processingTime: number | null
  credits: number
  reason?: string
}

export type V3HistoryItem = V3Verification | V3Generation

export interface V3HistoryFilters {
  page?: number
  limit?: number
  status?: 'all' | 'success' | 'failed'
  search?: string
  startDate?: string
  endDate?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

export interface V3HistoryResponse {
  success: boolean
  data: {
    history: V3HistoryItem[]
    pagination: {
      currentPage: number
      totalPages: number
      totalItems: number
      itemsPerPage: number
      hasNextPage: boolean
      hasPreviousPage: boolean
    }
  }
}

export const v3VerificationHistoryAPI = {
  async getVerifications(filters: V3HistoryFilters = {}): Promise<V3HistoryResponse> {
    const params = new URLSearchParams()
    params.append('type', 'all')   // include both /verify/* and /generate/*
    if (filters.page) params.append('page', String(filters.page))
    if (filters.limit) params.append('limit', String(filters.limit))
    if (filters.status && filters.status !== 'all') params.append('status', filters.status)
    if (filters.search) params.append('search', filters.search)
    if (filters.startDate) params.append('startDate', filters.startDate)
    if (filters.endDate) params.append('endDate', filters.endDate)
    if (filters.sortBy) params.append('sortBy', filters.sortBy)
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder)

    const response = await axiosInstance.get<V3HistoryResponse>(
      `/api/v3/history/verifications?${params.toString()}`
    )
    return response.data
  }
}
